import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../hooks/useApi';
import './AdminPlanDefinitions.css';

const GROUP_LABELS = {
    price:           'Цена',
    analytics:       'Аналитика',
    seo:             'SEO',
    prices:          'Цены и мониторинг',
    ads:             'Реклама',
    logistics:       'Логистика',
    projects:        'Проекты',
    ai:              'AI',
    finance:         'Финансы',
    tools:           'Инструменты',
    ai_agent:        'AI Агент',
    ozon:            'OZON',
    wildberries:     'Wildberries',
    crm:             'CRM',
    tasks:           'Задачи',
    proj_assortment: 'Проекты — Ассортимент',
    proj_tables:     'Проекты — Таблицы проекта',
    ozon_assortment: 'OZON — Ассортимент',
    ozon_seo:        'OZON — SEO',
    ozon_analytics:  'OZON — Аналитика',
    ozon_prices:     'OZON — Цены',
    ozon_repricer:   'OZON — Репрайсер',
    ozon_logistics:  'OZON — Логистика',
    ozon_ads:        'OZON — Реклама',
    ozon_finance:    'OZON — Финансы',
    wb_analytics:    'WB — Статистика',
};

function IntCell({ value, onChange }) {
    const display = value === -1 ? '∞' : String(value ?? 0);
    return (
        <div className="pdc__int-cell">
            <input
                type="number"
                className="pdc__int-input"
                value={value === -1 ? '' : (value ?? 0)}
                placeholder={value === -1 ? '∞' : ''}
                min="-1"
                onChange={e => {
                    const v = e.target.value === '' ? -1 : parseInt(e.target.value, 10);
                    onChange(isNaN(v) ? 0 : v);
                }}
            />
            {value === -1 && <span className="pdc__unlim-badge">∞</span>}
        </div>
    );
}

function PriceCell({ value, onChange }) {
    return (
        <input
            type="number"
            className="pdc__price-input"
            value={value ?? 0}
            min="0"
            step="0.01"
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
        />
    );
}

function BoolCell({ value, onChange }) {
    return (
        <button
            className={`pdc__bool-btn ${value ? 'pdc__bool-btn--on' : 'pdc__bool-btn--off'}`}
            onClick={() => onChange(!value)}
            title={value ? 'Включено — нажми чтобы отключить' : 'Отключено — нажми чтобы включить'}
        >
            {value ? '✓' : '✗'}
        </button>
    );
}

const EMPTY_PLAN = { key: '', label: '', price_monthly: 0, price_daily: 0, order: 1 };

export default function AdminPlanDefinitions() {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [plans, setPlans] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [catalogSaving, setCatalogSaving] = useState(false);
    const [catalogSaved, setCatalogSaved] = useState(false);
    const [catalogError, setCatalogError] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await request('/api/v1/admin/plan-definitions');
            setData(res);
            setPlans(JSON.parse(JSON.stringify(res.plans)));
            const cat = JSON.parse(JSON.stringify(res.plan_catalog || []));
            console.log('Loaded catalog:', cat);
            setCatalog(cat);
        } catch (e) {
            setError('Ошибка загрузки: ' + (e.message || e));
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    const updateFeature = (planKey, featureKey, value) => {
        setPlans(prev => ({
            ...prev,
            [planKey]: { ...prev[planKey], [featureKey]: value },
        }));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await request('/api/v1/admin/plan-definitions', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(plans),
            });
            setData(res);
            setPlans(JSON.parse(JSON.stringify(res.plans)));
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            setError('Ошибка сохранения: ' + (e.message || e));
        } finally {
            setSaving(false);
        }
    };

    const updateCatalog = (idx, field, val) => {
        setCatalog(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));
        setCatalogSaved(false);
    };

    const addPlan = () => {
        if (catalog.length >= 6) return;
        setCatalog(prev => [...prev, { ...EMPTY_PLAN, order: prev.length + 1 }]);
    };

    const removePlan = idx => setCatalog(prev => prev.filter((_, i) => i !== idx));

    const handleCatalogSave = async () => {
        setCatalogSaving(true);
        setCatalogError('');
        try {
            const clean = catalog.map((p, i) => ({
                key:           String(p.key).trim().toLowerCase().replace(/\s+/g, '_'),
                label:         String(p.label).trim(),
                price_monthly: Number(p.price_monthly) || 0,
                price_daily:   Number(p.price_daily)   || 0,
                order:         Number(p.order) || i + 1,
                popular:       !!p.popular,
            }));
            if (clean.some(p => !p.key || !p.label)) { setCatalogError('Заполни ключ и название.'); return; }
            const keys = clean.map(p => p.key);
            if (new Set(keys).size !== keys.length) { setCatalogError('Ключи должны быть уникальными.'); return; }
            await request('/api/v1/admin/plan-catalog', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(clean),
            });
            setCatalogSaved(true);
            setTimeout(() => setCatalogSaved(false), 3000);
            await load();
        } catch (e) {
            setCatalogError('Ошибка: ' + (e.message || e));
        } finally {
            setCatalogSaving(false);
        }
    };

    if (!data || !plans) {
        return <div className="pdc__loading">Загрузка тарифов…</div>;
    }

    const { feature_meta, plan_order, plan_labels } = data;

    // Группируем фичи. Лейбл группы приходит с бэкенда (group_label, построен из
    // CATEGORY_DEFAULTS/SECTION_DEFAULTS — тот же источник, что и /admin/tool-registry),
    // GROUP_LABELS ниже остаётся только как fallback на случай старого ответа сервера.
    const groups = {};
    const groupLabels = {};
    for (const feat of feature_meta) {
        if (!groups[feat.group]) groups[feat.group] = [];
        groups[feat.group].push(feat);
        if (feat.group_label) groupLabels[feat.group] = feat.group_label;
    }

    return (
        <div className="pdc">
            <div className="pdc__header">
                <h2 className="pdc__title">Конструктор тарифов</h2>
                <div className="pdc__actions">
                    {saved && <span className="pdc__saved-badge">Сохранено ✓</span>}
                    {error && <span className="pdc__error-badge">{error}</span>}
                    <button
                        className="pdc__save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Сохраняю…' : 'Сохранить'}
                    </button>
                </div>
            </div>

            <div className="pdc__hint">
                Для целочисленных лимитов: <code>-1</code> = безлимит, <code>0</code> = отключено.
                Изменения вступают в силу в течение 60 секунд.
            </div>

            <div className="pdc__table-wrap">
                <table className="pdc__table">
                    <thead>
                        <tr>
                            <th className="pdc__th-feature">Функция</th>
                            {plan_order.map(pk => (
                                <th key={pk} className={`pdc__th-plan pdc__th-plan--${pk}`}>
                                    {plan_labels[pk]}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(groups).map(([groupKey, feats]) => (
                            <React.Fragment key={groupKey}>
                                <tr className="pdc__group-row">
                                    <td colSpan={plan_order.length + 1} className="pdc__group-label">
                                        {groupLabels[groupKey] || GROUP_LABELS[groupKey] || groupKey}
                                    </td>
                                </tr>
                                {feats.map(feat => (
                                    <tr
                                        key={feat.key}
                                        className={`pdc__feat-row ${feat.module_disabled ? 'pdc__feat-row--disabled' : ''}`}
                                        title={feat.module_disabled ? 'Модуль отключён в admin/settings — инструмент скрыт от пользователей' : ''}
                                    >
                                        <td className="pdc__feat-name">
                                            {feat.label}
                                            {feat.module_disabled && (
                                                <span className="pdc__disabled-badge">модуль выкл.</span>
                                            )}
                                        </td>
                                        {plan_order.map(pk => {
                                            const val = plans[pk]?.[feat.key];
                                            return (
                                                <td key={pk} className="pdc__feat-cell">
                                                    {feat.type === 'bool' && (
                                                        <BoolCell
                                                            value={!!val}
                                                            onChange={v => updateFeature(pk, feat.key, v)}
                                                        />
                                                    )}
                                                    {feat.type === 'int' && (
                                                        <IntCell
                                                            value={val}
                                                            onChange={v => updateFeature(pk, feat.key, v)}
                                                        />
                                                    )}
                                                    {feat.type === 'price' && (
                                                        <PriceCell
                                                            value={val}
                                                            onChange={v => updateFeature(pk, feat.key, v)}
                                                        />
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pdc__footer">
                <button className="pdc__save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохраняю…' : 'Сохранить'}
                </button>
            </div>

            {/* ── Каталог тарифов ─────────────────────────────────────────── */}
            <div className="pdc__catalog">
                <div className="pdc__catalog-header">
                    <div>
                        <h3 className="pdc__catalog-title">Каталог тарифов</h3>
                        <p className="pdc__catalog-hint">1–6 тарифов с произвольными ключами и ценами. После смены ключа матрица не теряется — ключ используется в plan_type у магазинов.</p>
                    </div>
                    <div className="pdc__catalog-actions">
                        {catalogSaved && <span className="pdc__saved-badge">Сохранено ✓</span>}
                        {catalogError && <span className="pdc__error-badge">{catalogError}</span>}
                        <button className="pdc__save-btn" onClick={handleCatalogSave} disabled={catalogSaving}>
                            {catalogSaving ? 'Сохраняю…' : 'Сохранить каталог'}
                        </button>
                    </div>
                </div>
                <table className="pdc__table pdc__catalog-table">
                    <thead>
                        <tr>
                            {['Ключ', 'Название', '₽/мес', '₽/день (авто)', 'Порядок', 'Популярный', ''].map(h => (
                                <th key={h} className="pdc__th-feature" style={{ fontWeight: 600, fontSize: 12 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {catalog.map((plan, idx) => {
                            const daily = plan.price_monthly > 0 ? (plan.price_monthly / 30).toFixed(2) : '0.00';
                            return (
                                <tr key={idx} className="pdc__feat-row">
                                    <td className="pdc__feat-name">
                                        <input type="text" value={plan.key} placeholder="free" onChange={e => updateCatalog(idx, 'key', e.target.value)} style={{ width: 90, fontFamily: 'monospace', fontSize: 12 }} />
                                    </td>
                                    <td className="pdc__feat-cell">
                                        <input type="text" value={plan.label} placeholder="Бесплатный" onChange={e => updateCatalog(idx, 'label', e.target.value)} style={{ width: 130 }} />
                                    </td>
                                    <td className="pdc__feat-cell">
                                        <input type="number" value={plan.price_monthly} min="0" step="100" onChange={e => {
                                            const m = parseFloat(e.target.value) || 0;
                                            updateCatalog(idx, 'price_monthly', m);
                                            updateCatalog(idx, 'price_daily', parseFloat((m / 30).toFixed(2)));
                                        }} style={{ width: 90 }} />
                                    </td>
                                    <td className="pdc__feat-cell" style={{ color: 'var(--text-muted,#888)', fontSize: 12, paddingLeft: 10 }}>
                                        {daily} ₽
                                    </td>
                                    <td className="pdc__feat-cell">
                                        <input type="number" value={plan.order} min="1" onChange={e => updateCatalog(idx, 'order', parseInt(e.target.value, 10) || idx + 1)} style={{ width: 80, textAlign: 'center' }} />
                                    </td>
                                    <td className="pdc__feat-cell" style={{ textAlign: 'center' }}>
                                        <input
                                            type="radio"
                                            name="popular-plan"
                                            checked={!!plan.popular}
                                            onChange={() => {
                                                setCatalog(prev => prev.map((p, i) => ({ ...p, popular: i === idx })));
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </td>
                                    <td className="pdc__feat-cell">
                                        {catalog.length > 1 && (
                                            <button type="button" onClick={() => removePlan(idx)} style={{ color: '#e74c3c', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Удалить</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {catalog.length < 6 && (
                    <button type="button" className="ghost" onClick={addPlan} style={{ marginTop: 10, fontSize: 13 }}>+ Добавить тариф</button>
                )}
            </div>
        </div>
    );
}
